import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Link,
  SegmentedControl,
  StatusIndicator,
  Icon,
  Input,
  Alert,
  Table,
  Pagination,
  ExpandableSection,
  FormField,
  Autosuggest,
  TokenGroup,
  ProgressBar,
  Select,
  SelectProps,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useMemo, useState } from 'react';
import {
  CreateReportResponse,
  LoadedPlaceReport,
  PlaceReport,
  PlaceSearchItemWithName,
  REPORT_CARD_TASKS,
  ReportContactRequest,
  SearchItem,
  SearchRequest,
  StatisticStatus,
  Task,
  TASK_NAME_INFO_MAP,
  TASK_NAME_MAP,
} from '../../common/types';
import { StatisticBox } from '../../components/StatisticBox';
import ReportCardHeader from '../../components/ReportCardHeader';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createReport, createReports, getReport } from '../../common/api/reports';
import { LoadingOrError } from '../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { searchPlaces } from '../../common/api/places';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { DropdownStatusProps } from '@cloudscape-design/components/internal/components/dropdown-status';
import { LocalTopNavigation } from '../../common/navigation';
import AutoCompleteSearch, { Suggestion } from '../../components/AutoCompleteSearch';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';
import { StarsIcon } from '../../components/StarsIcon';
import ReportCardHomeHeader from '../../components/ReportCardHomeHeader';

export default function View() {
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);
  const [reports, setReports] = useState<CreateReportResponse[]>([]);

  const createMutation = useMutation({
    mutationFn: createReports,
    onSuccess: (data) => {
      setSelectedItems([]);
      setReports(data);
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const validPhoneNumber = useMemo(() => {
    if (!phoneNumber.trim()) {
      return false;
    }
    return phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
  }, [phoneNumber]);

  const validEmail = useMemo(() => {
    if (!email.trim()) {
      return false;
    }
    return email.trim().includes('@');
  }, [email]);

  const hasContactInfo = useMemo(() => {
    return validEmail || validPhoneNumber;
  }, [validEmail, validPhoneNumber]);

  const invalidContact = useMemo(() => {
    // if they have a first name but no contact info, invalid
    if (firstName.trim() && !hasContactInfo) {
      return true;
    }
    // if they have no first name but have contact info, invalid
    if (!firstName.trim() && hasContactInfo) {
      return true;
    }
    return false;
  }, [firstName, lastName, email, phoneNumber]);

  const anyContact = useMemo(() => {
    return !!(
      firstName.trim() ||
      lastName.trim() ||
      email.trim() ||
      phoneNumber.trim() ||
      title.trim()
    );
  }, [firstName, lastName, email, phoneNumber, title]);

  const handleCreate = () => {
    if (createMutation.isPending) {
      return;
    }
    if (selectedItems.length === 0) {
      return;
    }

    let contact: ReportContactRequest | undefined;
    if (anyContact) {
      if (invalidContact) {
        setShowErrors(true);
        return;
      }
      contact = {
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone_number: phoneNumber.trim() || undefined,
        title: title.trim() || undefined,
      };
    }
    setReports([]);
    createMutation.mutate({
      place_ids: selectedItems.map((item) => item.placeId),
      contact,
      business_type:
        businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      // try to find the best business type
      const firstItem = selectedItems[0];
      const googleTags = firstItem.tags;
      const businessType = getBusinessType(googleTags);
      if (businessType) {
        setBusinessType({ label: businessType, value: businessType });
      } else {
        setBusinessType({ label: 'Other', value: 'Other' });
      }
    }
  }, [selectedItems]);

  return (
    <>
      <LocalTopNavigation />
      <ViewLayout
        header={<ReportCardHomeHeader />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <Grid
          gridDefinition={[
            {
              colspan: {
                default: 12,
              },
            },
            {
              colspan: {
                default: 12,
              },
            },
          ]}
        >
          <Container
            header={
              <Header
                variant="h1"
                description="Enter a business name to create a Reputation Scorecard from Google Reviews"
                actions={
                  <Button variant="normal" href="https://ai.realtimefeedback.com/scorecard">
                    View Scorecards
                  </Button>
                }
              >
                Create a Reputation Scorecard
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="s">
              <AutoCompleteSearch
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={createMutation.isPending}
              />
              <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
                <FormField label="Business Type">
                  <Select
                    selectedOption={businessType}
                    options={BUSINESS_TYPES}
                    onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                    disabled={createMutation.isPending}
                    placeholder="Select a business type"
                  />
                </FormField>
              </Grid>
              <ExpandableSection headerText="Add contact information">
                <Grid
                  gridDefinition={[
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                  ]}
                >
                  <FormField
                    label="First Name"
                    errorText={
                      showErrors && anyContact && !firstName.trim()
                        ? 'First name is required'
                        : undefined
                    }
                  >
                    <Input
                      value={firstName}
                      onChange={({ detail }) => setFirstName(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Last Name">
                    <Input
                      value={lastName}
                      onChange={({ detail }) => setLastName(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Email"
                    errorText={
                      showErrors && anyContact && !hasContactInfo
                        ? 'Email or phone number is required'
                        : undefined
                    }
                  >
                    <Input
                      value={email}
                      onChange={({ detail }) => setEmail(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Phone Number"
                    errorText={
                      showErrors && anyContact && !hasContactInfo
                        ? 'Email or phone number is required'
                        : undefined
                    }
                  >
                    <Input
                      value={phoneNumber}
                      onChange={({ detail }) => setPhoneNumber(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Title">
                    <Input
                      value={title}
                      onChange={({ detail }) => setTitle(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                </Grid>
              </ExpandableSection>
              <Box float="right">
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={selectedItems.length === 0}
                >
                  {selectedItems.length > 1
                    ? `Create Scorecards (${selectedItems.length})`
                    : 'Create Scorecard'}
                </Button>
              </Box>
              {createMutation.isError && (
                <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
              )}
            </SpaceBetween>
          </Container>
          {reports.length > 0 && (
            <div>
              {/* TODO: add reports */}
              {reports.map((report) => (
                <ReportProgress key={report.uuid} uuid={report.uuid} />
              ))}
            </div>
          )}
        </Grid>
      </ViewLayout>
    </>
  );
}

export type ReportProgressProps = {
  uuid: string;
};

export function ReportProgress({ uuid }: ReportProgressProps) {
  const getQuery = useQuery({
    queryKey: ['reports', uuid],
    queryFn: () => getReport(uuid ?? ''),
    enabled: !!uuid,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return getQuery.isLoading || !getQuery.data || getQuery.error ? (
    <Container variant="stacked">
      <LoadingOrError
        loading={getQuery.isLoading}
        retry={() => getQuery.refetch()}
        error={getQuery.error}
      />
    </Container>
  ) : (
    <ReportStatus report={getQuery.data} />
  );
}

export type ReportStatusProps = {
  report: PlaceReport;
};

export function ReportStatus(props: ReportStatusProps) {
  const [report, setReport] = useState(props.report);
  const getReq = useMutation({
    mutationFn: () => getReport(props.report.uuid),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  const currentTask = useMemo(() => {
    if (report.tasks.length === 0) {
      return undefined;
    }
    return [...report.tasks]
      .filter((task) => REPORT_CARD_TASKS.includes(task.name))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }, [report.tasks]);

  const status = useMemo(() => {
    if (report.periods && report.periods.length > 0) {
      return 'success';
    }
    if (!currentTask) {
      return 'in-progress';
    }
    if (currentTask.status === 'pending') {
      return 'in-progress';
    }
    if (currentTask.status === 'in-progress') {
      return 'in-progress';
    }
    // check for specific task name
    if (currentTask.status === 'completed') {
      if (currentTask.name === 'report_statistics') {
        return 'success';
      }
      return 'in-progress';
    }
    if (currentTask.status === 'failed' && currentTask.retries && currentTask.retries >= 3) {
      return 'error';
    }
    return 'in-progress';
  }, [currentTask]);

  useEffect(() => {
    const pollInterval = setInterval(async () => {
      // TODO call poll function better to look for changes
      if (status === 'in-progress') {
        await getReq.mutateAsync();
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getReq, report, status]);

  return <ReportStatusProgress report={report} currentTask={currentTask} status={status} />;
}

export type ReportStatusProgressProps = {
  report: PlaceReport;
  currentTask?: Task;
  status: 'in-progress' | 'success' | 'error';
};

export function ReportStatusProgress({ report, currentTask, status }: ReportStatusProgressProps) {
  const navigate = useNavigate();
  const currentName = currentTask ? TASK_NAME_MAP[currentTask.name] : 'Collecting details...';
  const currentInfo = currentTask
    ? TASK_NAME_INFO_MAP[currentTask.name]
    : 'Collecting details from Google Maps...';
  const totalReviews = report.place?.total_review_count ?? 100;
  // report needs 180 days of reviews, so we're going to try to estimate how long it will take to collect them and make
  // it proportional to the total reviews. The task doesn't update live, so we will just have a timer refreshing every second
  // to show the progress.
  const [progress, setProgress] = useState<number>(0);

  // here we will start by checking how this works for current places.
  // first measurement: 10 seconds for 3500 review place
  // update frequency is every second
  const updateInterval = 1000;
  // 100 units to update for 10 seconds
  const updateUnitsPerSecond = 100.0 / 10;
  // now normalize to total reviews
  const reviewFactor = totalReviews / 3500;
  const placeFactor = (updateUnitsPerSecond * reviewFactor) / 5;
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        const newProgress = p + 1.0 * placeFactor;
        if (newProgress >= 99) {
          // don't show 100% until the task is actually done
          return 99;
        }
        return newProgress;
      });
    }, updateInterval);
    return () => clearInterval(interval);
  }, []);

  return (
    <Container
      variant="stacked"
      header={
        <Header
          variant="h3"
          description={
            report.place ? (
              <SpaceBetween direction="vertical" size="xxxs">
                {report.place.address}
                <SpaceBetween direction="horizontal" size="xs">
                  {report.place.rating?.toFixed(1) ?? '?.?'}
                  <StarsIcon stars={report.place.rating} />
                  <Link href={report.place.cid_url} target="_blank">
                    {report.place.total_review_count?.toLocaleString() ?? '?'} Google reviews
                  </Link>
                </SpaceBetween>
              </SpaceBetween>
            ) : (
              '...'
            )
          }
          actions={
            <Button
              variant="primary"
              disabled={status !== 'success'}
              href={`/${report.uuid}`}
              target="_blank"
            >
              View
            </Button>
          }
        >
          Creating Reputation Scorecard for {report.place?.name ?? '...'}
        </Header>
      }
    >
      <ProgressBar
        label={status === 'success' ? 'Completed creating scorecard' : currentName}
        // in-progress, success, or error
        status={status}
        // 0 to 100 as a percentage
        value={status === 'in-progress' ? progress : 100}
        additionalInfo={status === 'success' ? 'Scorecard created successfully' : currentInfo}
        resultText="Completed"
      />
    </Container>
  );
}
